import React, { useState, useEffect, useRef, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { createSelector } from "reselect";
import { useAuth } from "@clerk/clerk-react";
import Messages from "./Messages";
import { Sparkle, Loader2 } from "lucide-react";

import { getAssistantService } from "../../utils/assistantService";
import {
  createProjectThread,
  updateProjectThread,
} from "../../reducers/chatReducer";
import { clearAssistantUnavailable, createAssistant, setAssistantUnavailable } from "../../reducers/assistantReducer";
import { startProject } from "../../reducers/projectReducer";
import { getProjectThreadService } from "../../utils/projectService";
import {
  CHAT_UNAVAILABLE_CODE,
  isChatUnavailableError,
  isChatUnavailablePayload,
  responseToChatUnavailableError,
} from "../../utils/chatAvailability";
import MessagesContainer from "./MessagesContainer";
import useEventCallback from "../../hooks/useEventCallback";

const selectCurrentProject = (state) => state.projectList.currentProject;
const selectAssistant = (state) => state.assistant;
const selectChats = (state) => state.chats;

const selectCurrentChat = createSelector(
  [selectCurrentProject, selectChats],
  (currentProject, chats) => {
    const sortedChats = [...chats].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    return sortedChats.find((chat) => chat?.projectId === currentProject?.id && !chat?.taskId);
  }
);

const selectStoreInitialized = createSelector(
  [selectChats, selectAssistant],
  (chats, assistant) => ({
    isChatsInitialized: Array.isArray(chats),
    isAssistantInitialized: !!assistant?.id,
  })
);

const ChatSession = ({
  messageLoading,
  newMessages,
  projectRestart,
  setProjectRestart,
}) => {
  const dispatch = useDispatch();
  const { userId, getToken } = useAuth();

  const getAssistantCalled = useRef(false);
  const getThreadsCalled = useRef(false);
  const [assistantStatus, setAssistantStatus] = useState("offline");
  const [threadIsLoaded, setThreadIsLoaded] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);

  const currentProject = useSelector(selectCurrentProject);
  const assistant = useSelector(selectAssistant);
  const chat = useSelector(selectCurrentChat);
  const { isChatsInitialized, isAssistantInitialized } = useSelector(
    selectStoreInitialized
  );

  useEffect(() => {
    setThreadIsLoaded(false);
    getThreadsCalled.current = false;
    setChatMessages([]);
    setAssistantStatus("offline");
  }, [currentProject?.id]);

  useEffect(() => {
    if (newMessages?.length > 0) {
      setChatMessages((prevMessages) => {
        // If it's the first message, simply return it
        if (prevMessages.length === 0) {
          return newMessages;
        }

        // Create a copy of previous messages
        const updatedMessages = [...prevMessages];

        // If streaming an assistant message, update the last message
        if (newMessages[newMessages.length - 1].role === "assistant") {
          // Remove the duplicate user message if it exists and there are more than one messages
          const userMessageIndex = updatedMessages.findLastIndex(
            (msg) => msg.role === "user"
          );
          if (userMessageIndex !== -1 && updatedMessages.length > 1) {
            updatedMessages.splice(userMessageIndex, 1);
          }

          // Replace or update the last assistant message
          const lastAssistantIndex = updatedMessages.findLastIndex(
            (msg) => msg.role === "assistant"
          );
          if (lastAssistantIndex !== -1) {
            updatedMessages[lastAssistantIndex] =
              newMessages[newMessages.length - 1];
          } else {
            updatedMessages.push(newMessages[newMessages.length - 1]);
          }

          return updatedMessages;
        }

        // If new messages are not from assistant, append them
        return [...updatedMessages, ...newMessages];
      });
    }
  }, [newMessages]);

  const fetchOrCreateAssistant = useEventCallback(async () => {
    if (
      assistantStatus !== "offline" ||
      getAssistantCalled.current ||
      !isAssistantInitialized
    )
      return;

    getAssistantCalled.current = true;
    try {
      const response = await getAssistantService(userId);

      if (response.data.new) {
        dispatch(
          createAssistant({
            id: response.data.assistantId,
            name: response.data.assistantName,
          })
        );
      }
      setAssistantStatus("online");
    } catch (error) {
      console.error("Error creating assistant:", error);
      setAssistantStatus("offline");
    } finally {
      getAssistantCalled.current = false;
    }
  });

  // Assistant initialization is keyed to assistant readiness and user identity.
  useEffect(() => {
    fetchOrCreateAssistant();
  }, [
    assistantStatus,
    assistant?.id,
    fetchOrCreateAssistant,
    isAssistantInitialized,
    userId,
  ]);

  useEffect(() => {
    if (projectRestart) {
      console.log("Changing threadIsLoaded to false in ChatSession");
      setThreadIsLoaded(false);
    }
  }, [projectRestart]);

  const getOrCreateProjectThread = useEventCallback(async () => {
    if (
      !assistant?.id ||
      !currentProject?.id ||
      assistantStatus !== "online" ||
      !isChatsInitialized
    )
      return;

    if (getThreadsCalled.current || (threadIsLoaded && !projectRestart))
      return;

    getThreadsCalled.current = true;

    try {
      const response = await getProjectThreadService(getToken, {
        userId,
        assistantId: assistant.id,
        chatId: chat?.id || "",
        fileId: chat?.fileId || "",
        vectorStoreId: chat?.vectorStoreId || "",
        projectId: currentProject.id,
        step: !projectRestart ? "in_progress" : currentProject.step,
      });

      if (!response.ok) {
        throw await responseToChatUnavailableError(response);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let messageBuffer = "";
      let openingSucceeded = false;
      let availabilityRecovered = false;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        messageBuffer += decoder.decode(value, { stream: true });

        let separator;
        while ((separator = messageBuffer.indexOf("\n\n")) !== -1) {
          const event = messageBuffer.slice(0, separator).trim();
          messageBuffer = messageBuffer.slice(separator + 2);
          if (!event.startsWith("data:")) continue;
          let data;
          try { data = JSON.parse(event.replace(/^data:\s*/, "")); } catch { continue; }

          if (isChatUnavailablePayload(data)) {
            dispatch(setAssistantUnavailable({ reason: data.error.code }));
            return;
          }

          if (data.messages) {
            setChatMessages(data.messages);
            openingSucceeded = true;
          }

          if (data.chatId && data.fileId && data.vectorStoreId) {
            handleThreadData(data);
            openingSucceeded = true;
          }

          if (data.type === "chat_complete") {
            dispatch(clearAssistantUnavailable());
            availabilityRecovered = true;
          }
        }
      }
      if (openingSucceeded && !availabilityRecovered) {
        dispatch(clearAssistantUnavailable());
      }
    } catch (error) {
      if (isChatUnavailableError(error)) {
        dispatch(setAssistantUnavailable({ reason: error?.response?.data?.error?.code || CHAT_UNAVAILABLE_CODE }));
      }
      console.error("Error creating project thread:", error);
    } finally {
      getThreadsCalled.current = false;
      setThreadIsLoaded(true);
    }
  });

  // Thread initialization is keyed to project/assistant readiness. The event
  // callback supplies the latest chat identifiers without making Redux writes
  // recursively restart the request.
  useEffect(() => {
    if (assistantStatus === "online") {
      getOrCreateProjectThread();
    }
  }, [
    assistant?.id,
    assistantStatus,
    currentProject?.id,
    currentProject?.step,
    getOrCreateProjectThread,
    isChatsInitialized,
  ]);

  const handleThreadData = async (data) => {
    const isNewThread = !chat?.id;
    const isThreadRecreated =
      chat?.id &&
      (data.chatId !== chat?.id ||
        data.fileId !== chat?.fileId ||
        data.vectorStoreId !== chat?.vectorStoreId);

    if (isNewThread) {
      dispatch(
        createProjectThread({
          chatId: data.chatId,
          projectId: currentProject.id,
          fileId: data.fileId,
          vectorStoreId: data.vectorStoreId,
        })
      );
    } else if (isThreadRecreated) {
      if (projectRestart) {
        await new Promise((resolve) =>
          setTimeout(() => {
            dispatch(startProject(currentProject));
            resolve();
          }, 500)
        );
        setProjectRestart(false);
      }

      dispatch(
        updateProjectThread({
          chatId: chat.id,
          threadId: chat.threadId,
          fileId: data.fileId,
          vectorStoreId: data.vectorStoreId,
        })
      );
    }
  };

  const LoadingIndicator = useMemo(
    () => (
      <div className="flex mb-5 ml-4">
        <Sparkle className="pr-2 animate-ping" /> Thinking...
      </div>
    ),
    []
  );

  if (!isChatsInitialized || !isAssistantInitialized) {
    return (
      <div className="flex items-center justify-center p-4">
        <Loader2 className="h-6 w-6 animate-spin mr-2" />
        <span>Loading chat data...</span>
      </div>
    );
  }

  if (!threadIsLoaded) {
    return LoadingIndicator;
  }

  return (
    <MessagesContainer messageLoading={messageLoading}>
      {chatMessages?.map((msg, index) => (
        <Messages
          key={`${msg.role}-${index}`}
          id={msg.id}
          chatId={chat?.id}
          content={msg.content?.[0]?.text?.value || "Invalid content"}
          sender={msg.role || "unknown"}
        />
      ))}

      {/* Render Loading Indicator Safely */}
      {messageLoading && LoadingIndicator}
    </MessagesContainer>
  );
};

export default React.memo(ChatSession);
